import { useEffect, useMemo, useRef } from 'react';

import { graphql } from 'gatsby';

import { grid2Classes } from '@mui/material';

import { getRelevantFootnotesWithIndex } from './helper';
import { Footnote, StrapiFootnoteData } from './types';

export const query = graphql`
  fragment FootnotesFragment on Query {
    footnotes: allStrapiFootnote(filter: { locale: { eq: $locale } }) {
      nodes {
        strapi_id
        content {
          data {
            childMarkdownRemark {
              html
            }
          }
        }
      }
    }
  }
`;

/**
 *
 * @param footnoteReferences list of strapi_ids in the order in which they occur
 * @param allFootnotes list of all footnotes from strapi with their strapi_id
 * @returns map of all relevant footnote indexes (strapi_ids -> footnote index)
 */
export const useFootnotes = (
  footnoteReferences: string[],
  allFootnotes: StrapiFootnoteData['footnotes']['nodes'],
) => {
  const hasAppendToDOM = useRef(false);
  const relevantFootnotes = useMemo(
    () => getRelevantFootnotesWithIndex(footnoteReferences, allFootnotes),
    [footnoteReferences, allFootnotes],
  );

  useEffect(() => {
    if (hasAppendToDOM) {
      removeFootnotesFromDOM();
    }

    addFootnotesToDOM(relevantFootnotes);
    hasAppendToDOM.current = true;

    return () => {
      removeFootnotesFromDOM();
    };
  }, [relevantFootnotes]);

  return {
    footnoteIndexes: getFootnoteIndexes(relevantFootnotes),
  };
};

const getFootnoteIndexes = (relevantFootnotes: Footnote[]) =>
  Object.fromEntries(relevantFootnotes.map((f) => [f.strapi_id, f.index]));

const addFootnotesToDOM = (relevantFootnotes: Footnote[]) => {
  if (relevantFootnotes.length > 0) {
    const footerElement = document.querySelector(
      `footer div.${grid2Classes.container}`,
    );

    const footnotesContainerElement = document.createElement('ol');
    footnotesContainerElement.classList.add('footnotes');

    relevantFootnotes.forEach((footnote) => {
      // Add html elements for each footnote: <li><span>[id]</span><p>[html content]</p></li>
      const footnoteElement = document.createElement('li');
      const footnoteIdElement = document.createElement('span');
      const footnoteTextElement = document.createElement('p');

      footnoteIdElement.classList.add('counter');
      footnoteTextElement.classList.add('text');
      footnoteIdElement.textContent = footnote.index;
      footnoteTextElement.innerHTML = footnote.html;

      footnoteElement.appendChild(footnoteIdElement);
      footnoteElement.appendChild(footnoteTextElement);
      footnotesContainerElement.appendChild(footnoteElement);
    });

    footerElement?.appendChild(footnotesContainerElement);
  }
};

const removeFootnotesFromDOM = () => {
  const footnotesElement = document.querySelector('footer .footnotes');
  if (footnotesElement && footnotesElement.parentNode) {
    footnotesElement.parentNode.removeChild(footnotesElement);
  }
};
